import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useDrag } from 'react-dnd'
import { MAJOR_TYPES_PRESET, PITCHES } from './editorConstants'
import { getRealRoot, useKeyPressing } from './editorUtils'
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Paper, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, SvgIcon } from '@mui/material'
import { StyledTabs, StyledTab } from '../../components/StyledTabs'
import { makeStyles, withStyles } from '@mui/styles'
import StyledPopup from '../../components/StyledPopup'
import { StyledMenu, StyledMenuItem } from '../../components/StyledSelect'
import AllInclusive from '@mui/icons-material/AllInclusive'
import StyledDivider from '../../components/StyledDivider'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import './EditorLibrary.css'
import {ReactComponent as OneShotSelectedIcon} from './icons/one-shot-selected.svg'
import {ReactComponent as OneShotUnselectedIcon} from './icons/one-shot-unselected.svg'
import {ReactComponent as GrooveLoopIcon} from './icons/groove-loop.svg'
import {ReactComponent as GrooveOneShotIcon} from './icons/groove-one-shot.svg'
import {ReactComponent as LickLoopIcon} from './icons/lick-loop.svg'
import {ReactComponent as LickOneShotIcon} from './icons/lick-one-shot.svg'
import {ReactComponent as GroovePlayIcon} from './icons/groove-play.svg'
import {ReactComponent as LickPlayIcon} from './icons/lick-play.svg'
import {ReactComponent as GrooveStopIcon} from './icons/groove-stop.svg'
import {ReactComponent as LickStopIcon} from './icons/lick-stop.svg'

const LIBRARY_WIDTH = 320;
const ITEM_HEIGHT = 24;
const LIST_HEIGHT = 283;
const indicatorColors = ['#ff7100', '#f50058'];

const DenseTableCell = withStyles({
    root: {
        borderBottom: "none",
        color: 'white',
    },
    head: {
        padding: 3,
        pointerEvents: 'none',
        fontSize: 14
    },
    body: {
        padding: 2,
        fontSize: 12
    }
})(TableCell);

const useStyles = makeStyles(theme => ({
    table: {
        minWidth: (LIBRARY_WIDTH - 6).toString() + '.px',
        overflow: 'visible',
        background: '#A0A0A0',
        boxSizing: 'content-box',
        borderBottom: '7px solid #A0A0A0',
    },
    body: {
        '&:hover': {
            cursor: 'grab'
        }
    },
    row: {
        height: '24px',
        '&:hover': {
            backgroundColor: '#757575'
            // boxShadow: '0px 2px 4px 0px rgb(74, 74, 74, 1.0)'
        }
    },
    container: {
        height: '313px',
        background: '#A0A0A0',
        borderWidth: 0,
        borderRadius: 0,
        overflow: 'visible',
    },
    groovesTab: {
        color: indicatorColors[0] + ' !important',
    },
    licksTab: {
        color: indicatorColors[1] + ' !important',
    },
    popup: {
        minWidth: '145px',
        maxHeight: '24px',
        color: 'white',
        background: '#A0A0A0',
        "& > span": {
            fontSize: '11px'
        },
        '&:hover': {
            opacity: 0.7,
            background: '#A0A0A0'
        }
    },
    popper: {
        zIndex: theme.zIndex.modal + 1
    },
    menuItem: {
        fontSize: '11px',
        minWidth: '145px',
        justifyContent: 'flex-start',
        paddingTop: '6px',
        paddingBottom: '6px',
        '&$selected': {
            backgroundColor: 'white !important',
            color: theme.palette.primary.main
        },
    },
    selected: {}
}));

export default function EditorLibrary(props) {
    const {wavesurfer, showProgression, preview} = props;
    const categories = ['groove', 'lick'];
    const initialTypeFilter = 'Loops & One-Shots';
    const initialInstFilter = 'Any Instrument';
    const initialBpmFilter = 'Any BPM';
    const typeFilters = ['Loops', 'One-Shots'];
    const hasTypes = [false, false];
    const instFilters = [];
    const bpmFilters = ['Loop +/-10 BPM', 'Loop +/-20 BPM'];
    const classes = useStyles();
    const config = useSelector(state => state.editor.config);
    const clips = useSelector(state => state.editor.clips);
    const status = useSelector(state => state.editor.status);
    const key = useSelector(state => state.editor.key);
    const mode = useSelector(state => state.editor.mode);
    const bpm = useSelector(state => state.editor.bpm);
    const [tabIndex, setTabIndex] = useState(0);
    const [typeFilter, setTypeFilter] = useState(initialTypeFilter);
    const [instFilter, setInstFilter] = useState(initialInstFilter);
    const [bpmFilter, setBpmFilter] = useState(initialBpmFilter);
    const [typeFilterAnchorEl, setTypeFilterAnchorEl] = useState(null);
    const [instFilterAnchorEl, setInstFilterAnchorEl] = useState(null);
    const [bpmFilterAnchorEl, setBpmFilterAnchorEl] = useState(null);
    const [playRowIndex, setPlayRowIndex] = useState(-1);
    const scrollBarRef = useRef();
    const upKeyPressing = useKeyPressing('ArrowUp', 38);
    const downKeyPressing = useKeyPressing('ArrowDown', 40);
    const filteredItems = useRef([]);
    filteredItems.current = [];
    let currentRowIndex = 0;
    for (const group of config.groups)
        if (group.type === categories[tabIndex])
        {
            if (config.instruments && !instFilters.includes(group.instrument))
                instFilters.push(group.instrument)
            hasTypes[group.bpm === 0 ? 1 : 0] = true;
        }

    useEffect(() => {
        if ((downKeyPressing || upKeyPressing) && status === 'editing' && !showProgression)
        {
            const index = downKeyPressing && playRowIndex < filteredItems.current.length - 1 ? playRowIndex + 1 : (upKeyPressing && playRowIndex > 0 ? playRowIndex - 1 : -1);
            if (index === -1)
                return;

            playGroupPreview(filteredItems.current[index]);
            const instance = scrollBarRef.current.osInstance();
            if (instance.scroll().position.y >= (playRowIndex - 1) * ITEM_HEIGHT)
                instance.scroll({y : (playRowIndex - 1) * ITEM_HEIGHT});
            else if (instance.scroll().position.y + LIST_HEIGHT <= (playRowIndex + 2) * ITEM_HEIGHT)
                instance.scroll({ y : (playRowIndex + 2 ) * ITEM_HEIGHT - LIST_HEIGHT});
        }
      }, [downKeyPressing, upKeyPressing]);

    useEffect(() => {
        if (config.instruments && clips.length > 0)
        {
            const group = config.groups.find(group => group.id === clips[0].id && group.type === clips[0].type);
            setTabIndex(categories.indexOf(group.type));
            setInstFilter(group.instrument);
        }
    }, []);
    function playGroupPreview(item)
    {
        if (!preview)
            return;
        const {group, isPlaying, setPlaying, rowIndex} = item
        if (isPlaying)
            wavesurfer.stop();
        else if(status === 'editing')
        {
            if (wavesurfer.isPlaying())
                wavesurfer.stop();
            let previewUrl = preview + group.type + 's/' + group.id + '/';
            const position = group.positions[Math.floor((group.positions.length - 1) / 2)]
            if (group.type === 'groove')
            {
                const numeral_in_major = mode === 'major' ? 'I' : 'VI';
                if (group.numeral)
                {
                    const root = getRealRoot(group, key);
                    const sample = group.samples.find(sample => sample.pitch === root.pitch && sample.position === position && sample.numerals.includes(numeral_in_major));
                    previewUrl += key.replace('F#', 'Gb') + '-' + sample.numerals.join(',') + '-' + sample.position;
                }
                else
                    previewUrl += key.replace('F#', 'Gb') + (group.types !== null ? MAJOR_TYPES_PRESET[numeral_in_major].find(type => group.types.includes(type)) : '') + '-' + position;
            }
            else
                previewUrl += PITCHES[(PITCHES.indexOf(key) + (mode === 'major' ? 0 : 3)) % PITCHES.length].replace('F#', 'Gb') + '-' + position;
            previewUrl += '.mp3';
            wavesurfer.unAll();
            wavesurfer.load(previewUrl);
            wavesurfer.on("ready", () => {
                setPlaying(true);
                setPlayRowIndex(rowIndex);
                wavesurfer.play();
            });
            wavesurfer.on("pause", () => {
                setPlaying(false);
            });
            wavesurfer.on("finish", () => {
                setPlaying(false);
            });
        }
    }

    function GroupRow(group, index) {
        const rowIndex = currentRowIndex;
        const [, drag, preview] = useDrag(() => ({
            type: 'group',
            options: {
                dropEffect: 'move'
            },
            end: (item, monitor) => {
                if (monitor.didDrop())
                {
                    setHovering(false);
                    setPlayRowIndex(rowIndex);
                }

            },
            item: {
                id: group.id,
                name: group.name,
                type: group.type,
                beats: group.bpm === 0 ? 0 : group.length
            }
        }));
        const [isPlaying, setPlaying] = useState(false);
        const [isHovering, setHovering] = useState(false);

        const display = group.type === categories[tabIndex] &&
            (typeFilter === initialTypeFilter || group.bpm !== 0 ^ typeFilters.indexOf(typeFilter)) &&
            (instFilter === initialInstFilter || group.instrument === instFilter) &&
            (bpmFilter === initialBpmFilter || group.bpm === 0 || Math.abs(group.bpm - bpm) <= (bpmFilters.indexOf(bpmFilter) + 1) * 10);

        useEffect(() => {
            preview(getEmptyImage(), { captureDraggingState: true });
        }, [preview]);

        useEffect(() => {
            if (playRowIndex !== rowIndex || status !== 'editing')
                setPlaying(false);
        }, [playRowIndex, status, setPlaying])

        if (!display)
            return;

        filteredItems.current.push({group: group, isPlaying: isPlaying, setPlaying: setPlaying, rowIndex: rowIndex});
        const backgroundColor = playRowIndex === rowIndex ? (group.type === 'groove' ? '#FFB346' : '#FFBBD4') : (isHovering ? 'inherit' : (rowIndex % 2 === 0 ? '#888888' : '#A0A0A0'));
        currentRowIndex++ ;    

        return (
            <TableRow classes={{root: classes.row}} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} key={index} ref={drag} onClick={() => playGroupPreview(filteredItems.current[rowIndex])}>
                {/* <DragPreviewImage connect={preview} src={group.type === 'groove' ? (group.bpm === 0 ? GrooveOneShotImage : GrooveLoopImage) : (group.bpm === 0 ? LickOneShotImage : LickLoopImage)} /> */}
                <DenseTableCell align="center" style={{ minWidth: '36px', backgroundColor: backgroundColor }} >
                    <div style={{ paddingBottom: '3px', maxHeight: '14px', overflow: 'visible', position: 'relative' }}>
                        <SvgIcon component={isPlaying ? (group.type === 'groove' ? GrooveStopIcon : LickStopIcon)
                        : (isHovering ? (group.type === 'groove' ? GroovePlayIcon : LickPlayIcon) : (group.type === 'groove' ? (group.bpm === 0 ? GrooveOneShotIcon : GrooveLoopIcon) : (group.bpm === 0 ? LickOneShotIcon : LickLoopIcon)))} />
                    </div>
                </DenseTableCell>
                <DenseTableCell align="left" style={{ minWidth: '150px', backgroundColor: backgroundColor }}>{group.name !== undefined ? group.name : group.id}</DenseTableCell>
                <DenseTableCell align="center" style={{ maxWidth: '50px', backgroundColor: backgroundColor }} >{group.bpm !== 0 && group.length}</DenseTableCell>
                <DenseTableCell align="center" style={{ minWidth: '36px', backgroundColor: backgroundColor }} >{group.bpm !== 0 && group.bpm}</DenseTableCell>
            </TableRow>
        );
    }

    return (
        <div className={status !== 'editing' ? 'editor-library disabled' : 'editor-library'}>
            {!showProgression &&
            <StyledTabs
                value={tabIndex}
                onChange={(e, index) => {setTabIndex(index); setTypeFilter(initialTypeFilter); setInstFilter(initialInstFilter); setPlayRowIndex(-1);}}
                TabIndicatorProps={{
                    children: <div style={{ backgroundColor: indicatorColors[tabIndex] }} />,
                }}>
                <StyledTab disableTouchRipple label={'Grooves'} classes={tabIndex === 0 ? { root: classes.groovesTab } : {}} />
                <StyledTab disabled={config.groups.find(group => group.type === 'lick') === undefined} disableTouchRipple label={'Riffs & Licks'} classes={tabIndex === 1 ? { root: classes.licksTab } : {}} />
            </StyledTabs>}
            <div className='library-filters'>
                {config.instruments ? 
                <div>
                    <StyledPopup value={instFilter} open={Boolean(instFilterAnchorEl)} className={classes.popup} arrowColor={'white'}
                        handleClick={e => setInstFilterAnchorEl(Boolean(instFilterAnchorEl) ? null : e.currentTarget)} />
                    <StyledMenu
                        id="inst-filter-menu"
                        anchorEl={instFilterAnchorEl}
                        open={Boolean(instFilterAnchorEl)}
                        onClose={() => setInstFilterAnchorEl(null)}
                        transitionDuration={{ appear:150, enter: 150, exit: 0 }}
                    >
                        {instFilter !== initialInstFilter &&
                            <div>
                                <StyledMenuItem onClick={() => { setPlayRowIndex(-1); setInstFilterAnchorEl(null); setInstFilter(initialInstFilter); }}>RESET</StyledMenuItem>
                                <StyledDivider />
                            </div>
                        }
                        {instFilters.map((filter, index) => {
                            return (
                                <StyledMenuItem key={index} style={{ justifyContent: 'center' }} selected={filter === instFilter} classes={{ root: classes.menuItem, selected: classes.selected }}
                                    onClick={() => { setPlayRowIndex(-1); setInstFilterAnchorEl(null); setInstFilter(filter); }}>
                                    {filter}
                                </StyledMenuItem>);
                        })}
                    </StyledMenu>
                </div> :
                <div>
                    <StyledPopup value={typeFilter} open={Boolean(typeFilterAnchorEl)} className={classes.popup} arrowColor={'white'}
                        handleClick={e => setTypeFilterAnchorEl(Boolean(typeFilterAnchorEl) ? null : e.currentTarget)} />
                    <StyledMenu
                        id="type-filter-menu"
                        anchorEl={typeFilterAnchorEl}
                        open={Boolean(typeFilterAnchorEl)}
                        onClose={() => setTypeFilterAnchorEl(null)}
                        transitionDuration={{ appear:150, enter: 150, exit: 0 }}
                    >
                        {typeFilter !== initialTypeFilter &&
                            <div>
                                <StyledMenuItem onClick={() => { setTypeFilterAnchorEl(null); setTypeFilter(initialTypeFilter); setPlayRowIndex(-1); }}>RESET</StyledMenuItem>
                                <StyledDivider />
                            </div>
                        }
                        {typeFilters.map((filter, index) => {
                            return (hasTypes[index] && 
                                <StyledMenuItem key={index} selected={filter === typeFilter} classes={{ root: classes.menuItem, selected: classes.selected }}
                                    onClick={() => { setTypeFilterAnchorEl(null); setTypeFilter(filter); setPlayRowIndex(-1); }}>
                                    {index === 0 ? <AllInclusive style={{fontSize: '1.5em' }} /> : (filter === typeFilter ? <OneShotSelectedIcon /> : <OneShotUnselectedIcon /> )}
                                    <div style={{marginLeft: 5}}>{filter}</div>
                                </StyledMenuItem>);
                        })}
                    </StyledMenu>
                </div>
                }
                <div>
                    <StyledPopup value={bpmFilter} open={Boolean(bpmFilterAnchorEl)} className={classes.popup} arrowColor={'white'}
                        handleClick={e => setBpmFilterAnchorEl(Boolean(bpmFilterAnchorEl) ? null : e.currentTarget)} />
                    <StyledMenu
                        id="bpm-filter-menu"
                        anchorEl={bpmFilterAnchorEl}
                        open={Boolean(bpmFilterAnchorEl)}
                        onClose={() => setBpmFilterAnchorEl(null)}
                        transitionDuration={{ appear:150, enter: 150, exit: 0 }}
                    >
                        {bpmFilter !== initialBpmFilter &&
                            <div>
                                <StyledMenuItem onClick={() => { setPlayRowIndex(-1); setBpmFilterAnchorEl(null); setBpmFilter(initialBpmFilter); }}>RESET</StyledMenuItem>
                                <StyledDivider />
                            </div>
                        }
                        {bpmFilters.map((filter, index) => {
                            return (
                                <StyledMenuItem key={index} style={{ justifyContent: 'center' }} selected={filter === bpmFilter} classes={{ root: classes.menuItem, selected: classes.selected }}
                                    onClick={() => { setPlayRowIndex(-1); setBpmFilterAnchorEl(null); setBpmFilter(filter); }}>
                                    {filter}
                                </StyledMenuItem>);
                        })}
                    </StyledMenu>
                </div>
            </div>
            <OverlayScrollbarsComponent ref={scrollBarRef} options={{ className: 'os-theme-light', scrollbars: { autoHide: 'scroll' }}}>
                <TableContainer component={Paper} className={classes.container}>
                    <Table classes={{ root: classes.table }} size="small" aria-label="library table">
                        <TableHead>
                            <TableRow>
                                <DenseTableCell align='center' style={{ minWidth: '36px' }} >Type</DenseTableCell>
                                <DenseTableCell align='left' style={{ minWidth: '150px', overflow: 'hidden' }} >Name</DenseTableCell>
                                <DenseTableCell align='center' style={{ maxWidth: '50px' }} >Beats</DenseTableCell>
                                <DenseTableCell align='center' style={{ minWidth: '36px' }} >BPM</DenseTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody classes={{ root: classes.body}}>
                            {config.groups.map((group, index) => GroupRow(group, index))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </OverlayScrollbarsComponent>
        </div>);
}

